// Browser-only resolver implementations for the cover-page renderer.
//
// The ImageResolver/TokenResolver *interfaces* and the pure preview token
// resolver now live in @defectwise/pdf-report-core (shared with
// defects-management-reports-api). Only the browser-specific image resolver
// (fetch + canvas placeholder fallback, no server equivalent) stays here.
import type { ImageBox, ImageResolver } from "@defectwise/pdf-report-core";

export type { ImageBox, ImageResolver, TokenResolver } from "@defectwise/pdf-report-core";
export { defaultPreviewTokenResolver } from "@defectwise/pdf-report-core";

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Draws a labelled placeholder box on a canvas and returns it as a PNG data URL.
 * Used when an image can't be read as bytes (e.g. cross-origin hosted images
 * without CORS headers — pdfmake needs the bytes, which `fetch` can't read). The
 * canvas is drawn entirely by us, so it is never tainted and `toDataURL` is safe.
 */
function makePlaceholderImage(type: string, box?: ImageBox): string {
  const isLogo = type === "logo";
  // Use the target box when known so the placeholder has the exact aspect ratio;
  // fall back to the hosted placeholder sizes (logo 300x200, project 700x400).
  const width = box?.width && box.width > 0 ? Math.round(box.width) : isLogo ? 300 : 700;
  const height = box?.height && box.height > 0 ? Math.round(box.height) : isLogo ? 200 : 400;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  ctx.fillStyle = "#f3f0ea";
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = "#cdc8be";
  ctx.lineWidth = 3;
  ctx.setLineDash([10, 8]);
  ctx.strokeRect(6, 6, width - 12, height - 12);

  ctx.setLineDash([]);
  ctx.fillStyle = "#a39c90";
  ctx.font = "bold 28px Helvetica, Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    isLogo ? "LOGO" : type === "project-image" ? "PROJECT IMAGE" : "IMAGE",
    width / 2,
    height / 2,
  );

  return canvas.toDataURL("image/png");
}

/**
 * Default browser image resolver: fetch the src and inline it as a base64 data
 * URL. If the bytes can't be read (cross-origin without CORS, 404, network),
 * fall back to a synthetic placeholder box so the preview still renders an image.
 *
 * Results are cached by src — images are static and the page-break recompute runs
 * frequently while editing, so this avoids re-fetching/redrawing the same image.
 */
const imageCache = new Map<string, Promise<string>>();

export const defaultBrowserImageResolver: ImageResolver = (src, type, box) => {
  if (!src) return Promise.resolve("");

  const cached = imageCache.get(src);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`image fetch failed: ${response.status}`);
      return await blobToDataUrl(await response.blob());
    } catch {
      return makePlaceholderImage(type, box);
    }
  })();

  imageCache.set(src, promise);
  return promise;
};
